import { AI_ERRORS } from './aiErrors';
import type { ErrorDefinition, FriendlyError } from './types';

/**
 * Error definitions for the onboarding Quick Win grading flow
 * (`/api/questions/random-onboarding` + `/api/grade`).
 *
 * Reuses the shared `AI_ERRORS` map for AI-grader failures; layers on
 * onboarding-specific copy for `NOT_FOUND` (no question available),
 * question loading, and the usual auth / validation / network codes.
 */
export const ONBOARDING_GRADING_ERRORS: Record<string, ErrorDefinition> = {
  ...AI_ERRORS,

  // ---------------------------------------------------------
  // Backend codes (UPPERCASE, sent in apiError.code)
  // ---------------------------------------------------------
  AUTHENTICATION_ERROR: {
    title: 'Session expired',
    message: 'Your session has expired.',
    action: 'Please sign in again to continue.',
    retryable: false,
    category: 'authentication',
  },

  VALIDATION_ERROR: {
    title: 'Invalid answer',
    message: "We couldn't read that answer.",
    action: 'Please try recording or typing again.',
    retryable: true,
    category: 'validation',
  },

  NOT_FOUND: {
    title: 'Question unavailable',
    message:
      "We couldn't load a practice question right now. We've been notified and are looking into it.",
    action: 'Please try again.',
    retryable: true,
    category: 'server',
  },

  RATE_LIMIT_EXCEEDED: {
    title: 'Slow down a moment',
    message: "You've sent answers a little too quickly.",
    action: 'Please wait a few seconds and try again.',
    retryable: true,
    retryAfterSeconds: 10,
    category: 'rate_limit',
  },

  INTERNAL_ERROR: {
    title: 'Something went wrong',
    message:
      "We had trouble grading that answer. We've been notified and are looking into it.",
    action: 'Please try again in a moment.',
    retryable: true,
    retryAfterSeconds: 5,
    category: 'server',
  },

  // ---------------------------------------------------------
  // Client fallback codes (lowercase)
  // ---------------------------------------------------------
  question_load_failed: {
    title: "Couldn't load a question",
    message: 'We had trouble loading a practice question.',
    action: 'Please check your connection and try again.',
    retryable: true,
    category: 'network',
  },

  grading_failed: {
    title: "Couldn't grade your answer",
    message: 'We had trouble reaching our grader.',
    action: 'Please check your connection and try again.',
    retryable: true,
    category: 'network',
  },

  network_offline: {
    title: "You're offline",
    message: 'Grading your answer requires an internet connection.',
    action: 'Please connect to the internet and try again.',
    retryable: true,
    category: 'network',
  },
};

/**
 * Get a friendly error for an onboarding grading-related error code.
 * Returns the offline entry when the device is offline, regardless of input code.
 */
export function getOnboardingGradingError(code: string): FriendlyError {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return {
      ...ONBOARDING_GRADING_ERRORS.network_offline,
      code: 'network_offline',
    };
  }

  const errorDef = ONBOARDING_GRADING_ERRORS[code] ?? {
    title: "Couldn't grade your answer",
    message:
      "An unexpected error occurred. We've been notified and are looking into it.",
    action: 'Please try again.',
    retryable: true,
    category: 'unknown' as const,
  };

  return {
    ...errorDef,
    code,
  };
}
